import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

const ffmpeg = new FFmpeg();

async function processFile(file, onProgress) {
  const handleProgress = ({ progress }) => {
    onProgress(Math.floor(progress * 100));
  };

  try {
    const raw = file?.rawFile || file;
    if (!raw) throw new Error('Arquivo de entrada inválido');

    // Verificar se o arquivo tem conteúdo válido
    if (raw.size === 0) {
      throw new Error('Arquivo vazio');
    }

    // Verificar tamanho do arquivo (limite de 10MB para evitar problemas de memória)
    if (raw.size > 10 * 1024 * 1024) {
      throw new Error('Arquivo muito grande. Limite: 10MB');
    }

    // Verificar se FFmpeg está carregado corretamente
    if (!ffmpeg.loaded) {
      console.log('Carregando FFmpeg...');
      await ffmpeg.load();
      
      // Aguardar um pouco para garantir que carregou completamente
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    ffmpeg.on("progress", handleProgress);

    // Detectar tipo de arquivo
    const fileName = raw.name.toLowerCase();
    const fileType = raw.type.toLowerCase();
    
    const isImage = fileType.startsWith('image/') || 
                   fileName.match(/\.(jpg|jpeg|png|webp|bmp|tiff?)$/i);
    const isGif = fileType === 'image/gif' || fileName.endsWith('.gif');
    const isVideo = fileType.startsWith('video/') || 
                   fileName.match(/\.(mp4|mov|avi|mkv|webm|flv|wmv|m4v)$/i);

    if (!isImage && !isVideo && !isGif) {
      throw new Error('Formato de arquivo não suportado');
    }

    // Nome do arquivo de entrada (com extensão para ajudar o FFmpeg)
    let inputFileName;
    if (isImage && !isGif) {
      inputFileName = 'input.jpg';
    } else if (isGif) {
      inputFileName = 'input.gif';
    } else {
      inputFileName = 'input.mp4';
    }
    
    await ffmpeg.writeFile(inputFileName, await fetchFile(raw));

    let outputFileName;
    let outputMimeType;
    let processedFileName;

    if (isImage && !isGif) {
      // IMAGEM → WebP
      outputFileName = 'output.webp';
      outputMimeType = 'image/webp';
      processedFileName = 'processed.webp';

      await ffmpeg.exec([
        '-i', inputFileName,
        '-quality', '85',
        outputFileName
      ]);

    } else {
      // GIF ou VÍDEO → MP4
      outputFileName = 'output.mp4';
      outputMimeType = 'video/mp4';
      processedFileName = 'processed.mp4';

      if (isGif) {
        // Para GIF, usar parâmetros mais simples e seguros
        await ffmpeg.exec([
          '-i', inputFileName,
          '-c:v', 'libx264',
          '-preset', 'medium',
          '-crf', '23',
          outputFileName
        ]);
      } else {
        // Para vídeos normais
        await ffmpeg.exec([
          '-i', inputFileName,
          '-c:v', 'libx264',
          '-preset', 'medium',
          '-crf', '23',
          outputFileName
        ]);
      }
    }

    // Ler arquivo processado
    const data = await ffmpeg.readFile(outputFileName);

    // Limpar arquivos temporários
    try {
      await ffmpeg.deleteFile(inputFileName);
      await ffmpeg.deleteFile(outputFileName);
    } catch (cleanupError) {
      console.log('Erro ao limpar arquivos temporários:', cleanupError);
    }

    console.log("🚀 ~ processFile ~ outputMimeType:", outputMimeType)
    return new File([data], processedFileName, { type: outputMimeType });

  } catch (error) {
    console.error('Erro ao processar arquivo:', error);
    
    // Tentar limpar arquivos em caso de erro
    try {
      await ffmpeg.deleteFile('input.jpg');
      await ffmpeg.deleteFile('input.gif'); 
      await ffmpeg.deleteFile('input.mp4');
      await ffmpeg.deleteFile('output.webp');
      await ffmpeg.deleteFile('output.mp4');
    } catch (cleanupError) {
      // Ignorar erros de limpeza
    }
    
    // Mapear erros comuns
    const errorMessage = error.message || error.toString() || 'Erro desconhecido';
    
    if (errorMessage.includes('memory access out of bounds')) {
      throw new Error('Arquivo corrompido ou formato não suportado');
    }
    if (errorMessage.includes('Invalid data found')) {
      throw new Error('Formato de arquivo inválido');
    }
    
    throw new Error(`Falha no processamento: ${errorMessage}`);
  } finally {
    ffmpeg.off("progress", handleProgress);
    onProgress(0);
  }
}

export default processFile;